import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@services/api';
import type { LogEntry } from '../types/index';
import { DataTable } from '@components/DataTable';
import { Search, Download, RefreshCw, Home } from 'lucide-react';
import { format } from 'date-fns';

export const Logs: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  // Fetch logs from live database
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['logs', page, pageSize],
    queryFn: async () => {
      try {
        const response = await apiClient.getLogs(page, pageSize);
        return response;
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message || 'Failed to fetch logs'
        );
      }
    },
    staleTime: 10000,
    retry: 3,
  });

  // Fetch log statistics
  const { data: stats } = useQuery({
    queryKey: ['log-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.getLogStats();
        return response;
      } catch (err) {
        console.error('Failed to fetch log stats:', err);
        return null;
      }
    },
    staleTime: 30000,
  });

  // Filter logs based on search and level
  const filteredLogs = React.useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((log: LogEntry) => {
      const matchesSearch =
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.service.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesLevel = levelFilter === 'all' || log.level === levelFilter;

      return matchesSearch && matchesLevel;
    });
  }, [data, searchTerm, levelFilter]);

  const handleExport = async () => {
    try {
      const csvData = await apiClient.exportLogs('csv');
      const url = window.URL.createObjectURL(new Blob([csvData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'debug':
        return 'bg-gray-100 text-gray-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'warn':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'timestamp' as const,
      label: 'Timestamp',
      width: '180px',
      render: (value: string) =>
        format(new Date(value), 'MMM dd, yyyy HH:mm:ss'),
    },
    {
      key: 'level' as const,
      label: 'Level',
      width: '100px',
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${getLevelColor(
            value
          )}`}
        >
          {value.toUpperCase()}
        </span>
      ),
    },
    {
      key: 'service' as const,
      label: 'Service',
      width: '150px',
    },
    {
      key: 'message' as const,
      label: 'Message',
      width: '400px',
      render: (value: string) => (
        <span className="truncate" title={value}>
          {value}
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
              <p className="text-gray-600 mt-1">
                Monitor system activity and troubleshoot issues
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600 text-sm">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.total?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600 text-sm">Debug</p>
              <p className="text-2xl font-bold text-gray-600">
                {stats.debug || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600 text-sm">Info</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.info || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600 text-sm">Warnings</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.warn || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600 text-sm">Errors</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.error || 0}
              </p>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Level Filter */}
            <select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">All Levels</option>
              <option value="debug">Debug</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className="text-sm text-gray-600">
            {isLoading ? (
              'Loading...'
            ) : error ? (
              <span className="text-red-600">Error: {error.message}</span>
            ) : (
              <>
                Showing {filteredLogs.length} of{' '}
                {data?.pagination?.total || 0} logs
              </>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredLogs}
            loading={isLoading}
            error={error?.message}
            rowKey="id"
            onRowClick={(log) => setSelectedLog(log)}
            pagination={{
              page,
              pageSize,
              total: data?.pagination?.total || 0,
              onPageChange: setPage,
            }}
          />
        </div>

        {/* Log Detail Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Log Details
                </h2>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Timestamp</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {format(
                        new Date(selectedLog.timestamp),
                        'MMM dd, yyyy HH:mm:ss'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Level</p>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${getLevelColor(
                          selectedLog.level
                        )}`}
                      >
                        {selectedLog.level.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedLog.service}
                    </p>
                  </div>
                </div>

                {/* Message */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600 mb-2">Message</p>
                  <p className="text-sm bg-gray-50 p-3 rounded font-mono text-gray-900 break-words">
                    {selectedLog.message}
                  </p>
                </div>

                {/* Metadata */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600 mb-2">Metadata</p>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-40 text-gray-900">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Stack Trace */}
                {selectedLog.stackTrace && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600 mb-2">Stack Trace</p>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-40 text-gray-900 font-mono">
                      {selectedLog.stackTrace}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
